import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, X, Loader2, Film, Clapperboard, CheckCircle2, AlertCircle, Play, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useJobs } from '@/contexts/JobsContext';
import { useDock } from '@/contexts/DockContext';
import { DockTabs } from '@/components/UtilityDock';

const KIND_META = {
  video: { icon: Film, label: 'Video', color: '#E879F9' },
  stitch: { icon: Clapperboard, label: 'Final Cut', color: '#FF4500' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const ActivityPanel = () => {
  const { jobs, activeCount, refresh } = useJobs();
  const { tab, setTab } = useDock();
  const open = tab === 'activity';
  const setOpen = (v) => setTab(v ? 'activity' : null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const handleCancel = async (job) => {
    setCancellingId(job.id);
    try {
      const res = await api.post(`/projects/${job.project_id}/scenes/${job.scene_id}/cancel-video`);
      toast.info(res.data?.error || 'Generation cancelled');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not cancel'); }
    finally { setCancellingId(null); refresh(); }
  };
  const navigate = useNavigate();

  const handleView = (job) => {
    if (job.project_id && job.scene_id) {
      setOpen(false);
      navigate(`/project/${job.project_id}/scenes/${job.scene_id}`);
    }
  };

  const handleDownload = async (job) => {
    if (!job.output_path) return;
    setDownloadingId(job.id);
    try {
      const res = await api.get(`/files/${job.output_path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const ext = 'mp4';
      const fallback = `${(job.project_name || 'export').replace(/[^a-zA-Z0-9]+/g, '_')}.${ext}`;
      const a = document.createElement('a');
      a.href = url; a.download = job.output_name || fallback;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
    finally { setDownloadingId(null); }
  };

  return (
    <>
      {/* Overlay + Panel */}
      {open && (
        <div className="fixed inset-0 z-[70]" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            data-testid="activity-panel"
            onClick={e => e.stopPropagation()}
            className="absolute right-0 top-0 h-full w-full max-w-[400px] bg-[#0d0d11] border-l border-white/10 flex flex-col animate-slide-in-right"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#FF4500]" />
                <h3 className="text-white font-bold text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>Video Generation</h3>
                {activeCount > 0 && <span className="text-xs text-[#FF4500]">{activeCount} running</span>}
              </div>
              <div className="flex items-center gap-2"><DockTabs /><button data-testid="activity-panel-close" onClick={() => setOpen(false)} className="text-[#8A8A93] hover:text-white"><X className="w-5 h-5" /></button></div>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {jobs.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center text-[#55555C] gap-2 py-16">
                  <Activity className="w-8 h-8" />
                  <p className="text-sm">No video jobs yet</p>
                  <p className="text-xs">Scene video generation and Final Cut jobs will appear here.</p>
                </div>
              )}
              {jobs.map(job => {
                const meta = KIND_META[job.kind] || KIND_META.video;
                const Icon = meta.icon;
                const isProcessing = job.status === 'processing';
                const isComplete = job.status === 'complete';
                const isFailed = job.status === 'failed';
                const isCancelled = job.status === 'cancelled';
                const isSceneJob = job.kind === 'video';
                const title = job.kind === 'video' ? (job.scene_title || 'Scene') : `${meta.label} · ${job.project_name}`;
                return (
                  <div key={job.id} data-testid={`activity-job-${job.id}`} className="rounded-xl border border-white/[0.06] bg-[#141418] p-3">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}1a`, color: meta.color }}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {isSceneJob && job.project_id && job.scene_id ? (
                            <button data-testid={`activity-open-scene-${job.id}`} onClick={() => handleView(job)} className="text-sm text-white font-medium truncate hover:text-[#FF4500] text-left underline-offset-2 hover:underline" title="Open this scene">{title}</button>
                          ) : <p className="text-sm text-white font-medium truncate">{title}</p>}
                          {isComplete && <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981] shrink-0" />}
                          {isFailed && <AlertCircle className="w-3.5 h-3.5 text-[#EF4444] shrink-0" />}
                        </div>
                        <p className="text-[11px] text-[#8A8A93] truncate">
                          {isSceneJob && job.project_name ? `${job.project_name} · ` : ''}{timeAgo(job.created_at)}
                        </p>
                        {isProcessing && (
                          <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-[#FF4500]">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            <span className="truncate">{job.progress || 'Processing...'}{job.percent != null ? ` (${job.percent}%)` : ''}</span>
                            {isSceneJob && job.project_id && job.scene_id && (
                              <button data-testid={`activity-cancel-${job.id}`} onClick={() => handleCancel(job)} disabled={cancellingId === job.id}
                                className="ml-auto shrink-0 text-[10px] px-2 py-0.5 rounded border border-white/15 text-[#C9C9D1] hover:text-white hover:border-white/30 disabled:opacity-50">
                                {cancellingId === job.id ? '…' : 'Cancel'}
                              </button>
                            )}
                          </div>
                        )}
                        {isCancelled && <p className="text-[11px] text-[#8A8A93] mt-1 line-clamp-2">Cancelled{job.error ? ` — ${job.error}` : ''}</p>}
                        {isFailed && <p className="text-[11px] text-[#EF4444] mt-1 line-clamp-2">{job.error || 'Failed'}</p>}
                      </div>
                    </div>
                    {isComplete && (
                      <div className="mt-2 flex justify-end">
                        {isSceneJob ? (
                          <button data-testid={`activity-view-${job.id}`} onClick={() => handleView(job)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#E879F9]/15 text-[#E879F9] hover:bg-[#E879F9]/25 border border-[#E879F9]/30 font-medium">
                            <Play className="w-3.5 h-3.5" /> View Video
                          </button>
                        ) : (
                          <button data-testid={`activity-download-${job.id}`} onClick={() => handleDownload(job)} disabled={downloadingId === job.id}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 border border-[#10B981]/30 font-medium disabled:opacity-40">
                            {downloadingId === job.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                            Download{job.file_size_mb ? ` (${job.file_size_mb}MB)` : ''}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
